import type {
  CceAllocation, CsMethod, FilingStatus, HardshipLimitBase, MfsDeductionMethod, Mode, NetAdjustMethod,
  PartyIndex, SsMethod, Tactic, TacticSub, TaxYear, TimeshareMode, TimeshareUnit,
} from './enums.js';

// All money amounts in the case are MONTHLY (the original stores monthly floats and multiplies by 12
// for the annual tax forms). The UI converts when the display mode is ANNUAL.

/** "Other taxable income" detail window (also reused for new-spouse income). */
export interface InvestmentAndOtherIncome {
  capGainShort: number;
  capGainLong: number;
  rateGain28: number;
  unrecaptured1250: number;
  qualifiedDividends: number;
  nonQualifiedDividends: number;
  royalty: number;
  rental: number;
  interest: number;
  otherInvestment: number;
  socialSecurity: number;
  unemployment: number;
  alimonyReceivedFedTaxable: number;
  alimonyReceivedFedNonTaxable: number;
  partnerAlimonyReceived: number;
  /** "Other taxable income" free line. */
  other: number;
  subtractFromStateAgi: number;
  addToStateAgi: number;
}

export interface Exemptions {
  /** true = tally automatically from the children list (asterisk on screen). */
  auto: boolean;
  personal: number;
  special: number;
  newSpouse: number;
  dependent: number;
  qualifyingChildrenCcc: number;
  qualifyingChildrenEic: number;
  qualifyingChildrenCtc: number;
  childrenUnder6: number;
  otherDependents: number;
}

export interface WageDetail {
  weekly: number;
  biWeekly: number;
  semiMonthly: number;
  hourly: number;
  hoursPerWeek: number;
  beginDate?: string;
  endDate?: string;
}

export interface PartyTaxInfo {
  dateOfBirth?: string; // ISO yyyy-mm-dd
  blind: boolean;
  qualifiesCaFytc: boolean;
  newSpouseDateOfBirth?: string;
  newSpouseBlind: boolean;
  newSpouseQualifiesCaFytc: boolean;
  mfsLivedTogether: boolean;
  mfsQualifiedForEitc: boolean;
}

/** Tax settings: each employment/income tax can be switched off ("Exclude"). */
export interface PartyTaxSettings {
  socialSecurity: boolean;
  medicare: boolean;
  selfEmploymentTax: boolean;
  stateIncomeTax: boolean;
  stateDisabilityInsurance: boolean;
}

/** Tax credit override: true = Include (default), false = Exclude. */
export interface PartyCreditOverride {
  childCareCredit: boolean;
  earnedIncomeCredit: boolean;
  childTaxCredit: boolean;
  caExemptionCredit: boolean;
  caJchhCredit: boolean;
  caRentCredit: boolean;
  caChildCareCredit: boolean;
}

export interface AmtData {
  homeMortgageInterest: number;
  investmentInterest: number;
  depletion: number;
  netOperatingLoss: number;
  otherAdjustments: number;
}

export interface PartyInputs {
  /** Row 0: number of children of this relationship primarily with this parent. */
  childrenAsCustodial: number;
  /** Row 1: % of the year the OTHER parent's children spend with this parent (this parent as NCP), 0..100. */
  timeWithNcpPercent: number;
  /** Row 2 */
  filingStatus: FilingStatus;
  /** State filing status follows federal unless overridden (domestic partnerships). */
  stateFilingStatus?: FilingStatus;
  /** Row 3 */
  exemptions: Exemptions;
  /** Row 4 */
  wages: number;
  wageDetail: WageDetail;
  /** Row 5 */
  selfEmployment: number;
  /** Row 6 */
  otherTaxable: InvestmentAndOtherIncome;
  /** Row 7 */
  tanfReceived: number;
  childSupportReceived: number;
  /** Row 8 */
  nonTaxable: {
    forChildSupportOnly: number;
    taxExemptInterest: number;
    other: number;
    otherNonrefundableCredits: number;
    otherRefundableCredits: number;
  };
  /** Row 9 */
  newSpouseIncome: { wages: number; selfEmployment: number } & InvestmentAndOtherIncome;
  /** Row 10 */
  employee401k: number;
  /** Row 11 */
  adjustments: { sepSimple: number; otherAti: number; charitable: number };
  /** Row 12: spousal support paid re prior marriages. */
  ssPaidPrior: {
    thisSpouseFedDeductible: number;
    thisSpouseFedNonDeductible: number;
    newSpouseFedDeductible: number;
    newSpouseFedNonDeductible: number;
    partnerByThisSpouse: number;
    partnerByNewSpouse: number;
  };
  /** Row 13 */
  csPaidPrior: number;
  /** Row 14 */
  healthInsurance: { paidByParty: number; wageDeductionPreTax: number; caIsrPenalty: number };
  /** Row 15 */
  otherMedical: number;
  /** Row 16 */
  propertyTax: number;
  /** Row 17 */
  interestExpense: { investmentInterest: number; form4952Line4e: number; form4952Line4g: number; homeMortgage: number };
  /** Row 18 */
  contributions: number;
  /** Row 19 */
  miscDeductions: { stateSalesTax: number; investmentExpenses: number; other: number };
  /** Row 20 */
  qbid: number;
  /** Row 21 */
  unionDues: number;
  /** Row 22 */
  mandatoryRetirement: { qualifiedAti: number; other: number };
  /** Row 23 */
  hardship: {
    otherSupportedChildren: number;
    multiplyPercent: number;
    /** true = engine computes line 4 (asterisk). */
    calculate: boolean;
    totalFc4071b: number;
    extraordinaryHealth: number;
    catastrophicLosses: number;
  };
  /** Row 24 */
  otherGuidelineDeductions: { jobRelated: number; other: number };
  /** Row 25 */
  childCare: { thisRelationship: number; visitsSchoolTravel: number; uninsuredHealth: number; otherChildrenInHome: number };

  taxInfo: PartyTaxInfo;
  taxSettings: PartyTaxSettings;
  creditOverride: PartyCreditOverride;
  amt: AmtData;
  mfsDeductionMethod: MfsDeductionMethod;
  /**
   * Optional state-tax overrides (additive; absent = unchanged behaviour).
   * factorPercent scales the computed California income tax (100 = as computed).
   * userMonthlyAmount, when a number, replaces the computed California income tax (monthly dollars),
   * e.g. for a party taxed by another state.
   */
  stateTaxAdjustment?: { factorPercent: number; userMonthlyAmount: number | null };
  /**
   * Optional federal-tax overrides (additive; absent = unchanged behaviour). Mirrors stateTaxAdjustment:
   * userMonthlyAmount, when a number, replaces the computed federal income tax (monthly dollars); otherwise
   * factorPercent scales it (100 = as computed). Lets a case accept an independently prepared federal figure.
   */
  federalTaxAdjustment?: { factorPercent: number; userMonthlyAmount: number | null };
  /** Optional QBID worksheet (Form 8995/8995-A inputs). When present and `use` is true, apps set `qbid` from it. */
  qbidWorksheet?: QbidWorksheet;
}

export interface QbidBusiness {
  name: string;
  /** Annual qualified business income (may be negative). */
  qbi: number;
  /** Annual W-2 wages paid by the business. */
  w2Wages: number;
  /** Unadjusted basis immediately after acquisition of qualified property. */
  ubia: number;
  /** Specified service trade or business. */
  sstb: boolean;
}

export interface QbidWorksheet {
  use: boolean;
  businesses: QbidBusiness[];
  /** Annual qualified REIT dividends and PTP income. */
  reitPtp: number;
  /** Prior-year qualified business loss carryforward (annual, positive number). */
  lossCarryforward: number;
}

/** Timeshare worksheet: hours per calendar day of `year` that the children spend with `parent`. */
export interface TimeshareWorksheet {
  year: number;
  mode: TimeshareMode;
  parent: PartyIndex;
  /** Weekly pattern editor state: 7 days x 24 hours flags (or quarter-day steps in Santa Clara mode), for Year and alternating weeks. */
  pattern: { alternate: boolean; week1: number[]; week2: number[] };
  /** Hours per calendar day (365 or 366 entries). */
  hours: number[];
}

export interface Child {
  name: string;
  dateOfBirth?: string; // ISO
  custodialParent: PartyIndex;
  /** Time with the NON-custodial parent expressed as units of unitType per baseUnit (e.g. 73 DAY per YEAR). */
  timeshare: { units: number; unitType: TimeshareUnit; baseUnit: TimeshareUnit };
  /** Santa Clara table key when chosen from the table, or 'x'. */
  tableKey?: string;
  /** How the timeshare was set: direct entry, the Santa Clara table, or the worksheet. */
  timeshareSource?: 'DIRECT' | 'TABLE' | 'WORKSHEET';
  /** Attached worksheet (when timeshareSource = WORKSHEET the units are derived from it as HOUR per YEAR). */
  worksheet?: TimeshareWorksheet;
  /** User-entered CS for this child when CS method is USER. */
  userCs?: number;
  userCsPayor?: PartyIndex;
}

export interface CaseInfo {
  agencyName: string;
  branch: string;
  streetAddress: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  telephone: string;
  caseNumber: string;
  partyNames: [string, string];
  attorneys: [string, string];
  petitioner: PartyIndex | null;
  customString1: string;
  customString2: string;
}

export interface ProgramSettings {
  taxState: [number, number]; // 5 = California; only 5 implemented
  csMultiplier: number; // "Guideline non-deductible (child) support" factor
  ssMultiplier: number; // "Guideline deductible (spousal) support" factor
  includeSsAfter2018InFederal: boolean;
  /** SB 711 (Ch. 231, Stats. 2025): true when the order is executed on/after 1 Jan 2026, so California conforms to the federal rule and no longer deducts/taxes spousal support. Pre-SB711 behaviour (CA always deducts/taxes) is the default, which keeps parity with the original Xspouse. */
  caConformsAlimony: boolean;
  sameSexMarriage: boolean;
  domesticPartnership: boolean;
  csMethod: CsMethod;
  displayLowIncomeRange: boolean;
  fc4055Checking: boolean;
  applyCpiToLowIncomeThreshold: boolean;
  ssMethod: SsMethod;
  cceAllocation: CceAllocation;
  /** Party 0's share of add-ons (percent) when cceAllocation = CUSTOM. Optional/additive: absent ⇒ 50
   *  and no effect unless CUSTOM is selected, so existing cases compute exactly as before. */
  cceCustomPercent?: number;
  csOnAdjustedNets: boolean;
  ssOnAdjustedNets: boolean;
  netAdjustMethod: NetAdjustMethod;
  timeshareMode: TimeshareMode;
  hardshipLimitBase: HardshipLimitBase;
  hardshipLimitMultiplier: [number, number]; // percent
  releaseExemptions8332: boolean;
  /** "Bumped" spousal support (recipient made whole for tax on SS). */
  bumpSs: boolean;
  /**
   * Which FC 4055 guideline applies: the version before SB 343, the SB 343 version (effective 1 Sep 2024:
   * new K-factor bands, minimum-wage low-income threshold, FC 4061(b) add-on default), or AUTO (chosen from the
   * tax year). Optional and additive: when absent, engines behave exactly as before (pre-SB 343).
   */
  fc4055Version?: 'AUTO' | 'PRE_SB343' | 'SB343';
}

export interface UserSupport {
  /** Guideline column when csMethod = USER. */
  csAmount: number;
  csPayor: PartyIndex;
  /** Guideline column when ssMethod = USER. */
  ssAmount: number;
  ssPayor: PartyIndex;
  /** ssMethod = FORMULA parameters. */
  formula: { addPercentHighEarner: number; adjust: NetAdjustMethod; subtractPercentLowEarner: number; multiplyBy: number };
}

export interface ProposedSettings {
  tactic: Tactic;
  sub: TacticSub;
  /** Tactics 2/3: target net spendable $. */
  targetAmount: number;
  /** Tactics 4..7: percentage. */
  targetPercent: number;
  /** Tactic 1 and sub-options: user amounts (non-deductible = CS, deductible = SS). */
  userCs: number;
  userCsPayor: PartyIndex;
  userSs: number;
  userSsPayor: PartyIndex;
  /** Number of dependency exemptions to release via Form 8332 in the proposal (null = all available). */
  releases: number | null;
  /**
   * Optional: which children (indices into Case.children) are released via Form 8332 in the proposal.
   * When present it takes precedence over `releases` (count = length). Additive; absent = unchanged behaviour.
   */
  releaseChildren?: number[];
  comment: string;
}

export interface BonusAllocationRow { amount: number; csPercent: number; ssPercent: number; otherPercent: number }

export interface Case {
  id?: string;
  name?: string;
  taxYear: TaxYear;
  mode: Mode;
  info: CaseInfo;
  parties: [PartyInputs, PartyInputs];
  children: Child[];
  settings: ProgramSettings;
  userSupport: UserSupport;
  proposed: ProposedSettings;
  /**
   * Free-form per-application data stored with the case (e.g. appData.disOS = saved scenarios, caption extras,
   * report settings). Ignored by every engine. Keys are application names.
   */
  appData?: Record<string, unknown>;
  /** Bonus module data (optional). */
  bonus?: {
    periodsPerYear: [number, number];
    variableMonthly: [number[], number[]];
    table: [BonusAllocationRow[], BonusAllocationRow[]];
  };
}

// ---------------------------------------------------------------- defaults

const zeroIncome = (): InvestmentAndOtherIncome => ({
  capGainShort: 0, capGainLong: 0, rateGain28: 0, unrecaptured1250: 0, qualifiedDividends: 0, nonQualifiedDividends: 0,
  royalty: 0, rental: 0, interest: 0, otherInvestment: 0, socialSecurity: 0, unemployment: 0,
  alimonyReceivedFedTaxable: 0, alimonyReceivedFedNonTaxable: 0, partnerAlimonyReceived: 0, other: 0,
  subtractFromStateAgi: 0, addToStateAgi: 0,
});

export function defaultParty(index: PartyIndex): PartyInputs {
  return {
    childrenAsCustodial: 0,
    timeWithNcpPercent: 0,
    filingStatus: 'SINGLE',
    exemptions: { auto: true, personal: 1, special: 0, newSpouse: 0, dependent: 0, qualifyingChildrenCcc: 0, qualifyingChildrenEic: 0, qualifyingChildrenCtc: 0, childrenUnder6: 0, otherDependents: 0 },
    wages: 0,
    wageDetail: { weekly: 0, biWeekly: 0, semiMonthly: 0, hourly: 0, hoursPerWeek: 0 },
    selfEmployment: 0,
    otherTaxable: zeroIncome(),
    tanfReceived: 0,
    childSupportReceived: 0,
    nonTaxable: { forChildSupportOnly: 0, taxExemptInterest: 0, other: 0, otherNonrefundableCredits: 0, otherRefundableCredits: 0 },
    newSpouseIncome: { wages: 0, selfEmployment: 0, ...zeroIncome() },
    employee401k: 0,
    adjustments: { sepSimple: 0, otherAti: 0, charitable: 0 },
    ssPaidPrior: { thisSpouseFedDeductible: 0, thisSpouseFedNonDeductible: 0, newSpouseFedDeductible: 0, newSpouseFedNonDeductible: 0, partnerByThisSpouse: 0, partnerByNewSpouse: 0 },
    csPaidPrior: 0,
    healthInsurance: { paidByParty: 0, wageDeductionPreTax: 0, caIsrPenalty: 0 },
    otherMedical: 0,
    propertyTax: 0,
    interestExpense: { investmentInterest: 0, form4952Line4e: 0, form4952Line4g: 0, homeMortgage: 0 },
    contributions: 0,
    miscDeductions: { stateSalesTax: 0, investmentExpenses: 0, other: 0 },
    qbid: 0,
    unionDues: 0,
    mandatoryRetirement: { qualifiedAti: 0, other: 0 },
    hardship: { otherSupportedChildren: 0, multiplyPercent: 100, calculate: true, totalFc4071b: 0, extraordinaryHealth: 0, catastrophicLosses: 0 },
    otherGuidelineDeductions: { jobRelated: 0, other: 0 },
    childCare: { thisRelationship: 0, visitsSchoolTravel: 0, uninsuredHealth: 0, otherChildrenInHome: 0 },
    taxInfo: { blind: false, qualifiesCaFytc: false, newSpouseBlind: false, newSpouseQualifiesCaFytc: false, mfsLivedTogether: false, mfsQualifiedForEitc: false },
    taxSettings: { socialSecurity: true, medicare: true, selfEmploymentTax: true, stateIncomeTax: true, stateDisabilityInsurance: true },
    creditOverride: { childCareCredit: true, earnedIncomeCredit: true, childTaxCredit: true, caExemptionCredit: true, caJchhCredit: true, caRentCredit: true, caChildCareCredit: true },
    amt: { homeMortgageInterest: 0, investmentInterest: 0, depletion: 0, netOperatingLoss: 0, otherAdjustments: 0 },
    mfsDeductionMethod: 'STANDARD',
    ...(index === 1 ? {} : {}),
  };
}

/** Shipped program defaults recovered from the original (FUN_004a0104). */
export function defaultSettings(): ProgramSettings {
  return {
    taxState: [5, 5],
    csMultiplier: 1,
    ssMultiplier: 1,
    includeSsAfter2018InFederal: false,
    caConformsAlimony: false,
    sameSexMarriage: false,
    domesticPartnership: false,
    csMethod: 'GUIDELINE',
    displayLowIncomeRange: true,
    fc4055Checking: false,
    applyCpiToLowIncomeThreshold: true,
    ssMethod: 'SANTA_CLARA',
    cceAllocation: 'FIFTY_FIFTY',
    csOnAdjustedNets: false,
    ssOnAdjustedNets: true,
    netAdjustMethod: 'ACTUAL_CS',
    timeshareMode: 'HOURS',
    hardshipLimitBase: 'PRESUMED_CS',
    hardshipLimitMultiplier: [100, 100],
    releaseExemptions8332: true,
    bumpSs: false,
  };
}

export function defaultCaseInfo(): CaseInfo {
  return { agencyName: '', branch: '', streetAddress: '', city: '', county: '', state: 'CA', zip: '', telephone: '', caseNumber: '', partyNames: ['', ''], attorneys: ['', ''], petitioner: null, customString1: '', customString2: '' };
}

export function defaultCase(taxYear: TaxYear = 2024): Case {
  return {
    taxYear,
    mode: 'MONTHLY',
    info: defaultCaseInfo(),
    parties: [defaultParty(0), defaultParty(1)],
    children: [],
    settings: defaultSettings(),
    userSupport: { csAmount: 0, csPayor: 0, ssAmount: 0, ssPayor: 0, formula: { addPercentHighEarner: 40, adjust: 'PERCENT_ALLOCATED', subtractPercentLowEarner: 50, multiplyBy: 1 } },
    proposed: { tactic: '9', sub: 0, targetAmount: 0, targetPercent: 50, userCs: 0, userCsPayor: 0, userSs: 0, userSsPayor: 0, releases: null, comment: '' },
  };
}

/** Deep-merge a partial case (e.g. from storage or tests) over the defaults. */
export function normalizeCase(partial: unknown): Case {
  const base: any = defaultCase();
  const merge = (dst: any, src: any): any => {
    if (Array.isArray(src)) return src.map((v, i) => (dst && dst[i] !== undefined && typeof v === 'object' && v !== null ? merge(dst[i], v) : v));
    if (src && typeof src === 'object') {
      const out: any = { ...(dst ?? {}) };
      for (const k of Object.keys(src)) out[k] = merge(dst ? dst[k] : undefined, src[k]);
      return out;
    }
    return src === undefined ? dst : src;
  };
  const c = merge(base, partial ?? {}) as Case;
  // children entries need full defaults too
  c.children = (c.children ?? []).map((ch: any) => ({ name: '', custodialParent: 0, timeshare: { units: 0, unitType: 'DAY', baseUnit: 'YEAR' }, ...ch }));
  return c;
}

/** Child's time with the NCP as a fraction 0..1. */
export function childNcpFraction(ch: Child, unitMonths: Record<TimeshareUnit, number>): number {
  const f = (ch.timeshare.units * unitMonths[ch.timeshare.unitType]) / unitMonths[ch.timeshare.baseUnit];
  return Math.max(0, Math.min(1, f));
}
